import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '../../../core/types';
import { ROUTES } from '../../../core/data/routes';
import { useAppointmentsStore } from '../store/appointmentsStore';
import { AppBar, LoadingWidget, ZeroState, ShimmerAnimation, StatusChip, FadeInAnimation } from '../../../core/components/ui';
import { formatDate, formatTime } from '../../../core/utils/formatters';

type FilterType = 'ALL' | 'PENDING' | 'COMPLETED' | 'CANCELLED';

const BookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const store = useAppointmentsStore();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('ALL');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
  
      const result = await store.fetch();
  
      setSessions(result);
    } catch (error) {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filter === 'ALL'
    ? sessions
    : sessions.filter(s => (s.status || '').toUpperCase() === filter);

  const filters: FilterType[] = ['ALL', 'PENDING', 'COMPLETED', 'CANCELLED'];

  return (
    <div className="page">
      <AppBar title="My Bookings" onBack={() => navigate(-1)} />

      {/* Filter chips */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        borderBottom: '1px solid var(--color-outline-variant)',
        background: 'var(--color-surface)',
      }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 16px',
              borderRadius: 99,
              whiteSpace: 'nowrap',
              fontSize: 13,
              fontWeight: 600,
              background: filter === f ? 'var(--color-primary)' : 'var(--color-surface-variant)',
              color: filter === f ? 'white' : 'var(--color-on-surface-variant)',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
          >
            {f === 'ALL' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
            {' '}
            <span style={{ opacity: 0.7 }}>
              ({f === 'ALL' ? sessions.length : sessions.filter(s => (s.status || '').toUpperCase() === f).length})
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2].map(i => (
            <div key={i} className="card" style={{ display: 'flex', gap: 14 }}>
              <ShimmerAnimation width={44} height={44} borderRadius="50%" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <ShimmerAnimation width="50%" height={15} />
                <ShimmerAnimation width="70%" height={12} />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <ZeroState
          title="No Sessions Found"
          message={filter === 'ALL' ? 'You have no bookings yet.' : `No ${filter.toLowerCase()} sessions found.`}
        />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((s, i) => (
            <FadeInAnimation key={s.id || i} delay={i * 0.06}>
              <div
                className="card"
                onClick={() => navigate(ROUTES.UPCOMING_DETAILS, { state: { session: s } })}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && navigate(ROUTES.UPCOMING_DETAILS, { state: { session: s } })}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: 'var(--color-primary-container)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 14,
                    color: 'var(--color-on-primary-container)',
                    flexShrink: 0,
                  }}>
                    {(s.therapistName || '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{s.therapistName || 'Therapist'}</span>
                      <StatusChip status={s.status || 'PENDING'} />
                    </div>
                    {s.sessionFrom && (
                      <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        {formatDate(s.sessionFrom)} · {formatTime(s.sessionFrom)}
                      </div>
                    )}
                    {s.sessionReason && (
                      <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.sessionReason}
                      </div>
                    )}
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-on-surface-variant)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </div>
            </FadeInAnimation>
          ))}
          <div style={{ height: 16 }} />
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
